import {
  computed,
  nextTick,
  onUnmounted,
  reactive,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue';
import { isAxiosError } from 'axios';
import { useRouter } from 'vue-router';
import {
  clearBreadcrumbLabel,
  extractApiErrorMessage,
  setBreadcrumbLabel,
  useCustomToast,
  useUnsavedChangesGuard,
} from '@/shared/lib';
import { getRecordConfig, updateRecordConfig } from '../api/record-config.api';
import {
  buildPayload,
  createEmptyForm,
  emptySubjectSelection,
  formFromResponse,
  hasArchivingPermission,
  hasElectronicFormat,
  hasPhysicalFormat,
  hasRequiredRecordConfigFields,
  snapshotOf,
} from '../lib/record-config-form';
import {
  EDITABLE_IF_API_TYPE_NAME,
  FUNCTIONAL_INSPECTION_TYPE_LABELS,
  GD_ORIGIN_NAME,
} from '../lib/record-config-options';
import type { RecordConfigForm, RecordConfigMatrix, SubjectSelection } from './record-config.types';

const RECORD_LIST_ROUTE = { name: 'GestaoRegistrosListaRegistros' } as const;

/** ERP Protheus archives outside the application: the screen only reports it, never configures it. */
const AUTOMATIC_ARCHIVING: SubjectSelection = {
  mode: 'AUTOMATIC',
  positionIds: [],
  functionalActivityIds: [],
};

const CONFIRM_CLOSE_TITLE = 'Descartar alterações?';
const CONFIRM_CLOSE_DESCRIPTION = 'As alterações não salvas serão perdidas.';

const LOAD_FORBIDDEN_MESSAGE = 'Não foi possível abrir a configuração do registro';
const LOAD_ERROR_MESSAGE = 'Erro ao carregar a configuração. Tente novamente';
const SAVE_SUCCESS_MESSAGE = 'Configuração salva com sucesso';
const SAVE_ERROR_MESSAGE = 'Erro ao salvar configurações. Tente novamente';

const MISSING_REQUIRED_FIELDS_REASON = 'Preencha o título, as etapas e a indexação do registro';
const EMPTY_SUBJECT_SELECTION_REASON =
  'Selecione ao menos um cargo ou atividade funcional em cada seção com "Selecionar os Cargos e/ou Atividades Funcionais"';
const MISSING_DATE_ROUTE_REASON = 'Selecione o campo de data da 1ª Rota do arquivamento eletrônico';

export interface UseRecordConfig {
  matrix: Ref<RecordConfigMatrix | null>;
  form: RecordConfigForm;
  isLoading: Ref<boolean>;
  isSaving: Ref<boolean>;
  loadFailed: Ref<boolean>;
  isGdOrigin: ComputedRef<boolean>;
  isErpOrigin: ComputedRef<boolean>;
  isIfApiType: ComputedRef<boolean>;
  showsIndexing: ComputedRef<boolean>;
  functionalInspectionTypeLabel: ComputedRef<string | null>;
  isDirty: ComputedRef<boolean>;
  canSave: ComputedRef<boolean>;
  saveBlockedReason: ComputedRef<string | null>;
  showsElectronicBlock: ComputedRef<boolean>;
  showsPhysicalBlock: ComputedRef<boolean>;
  showsArchivingPermission: ComputedRef<boolean>;
  load: () => Promise<void>;
  save: () => Promise<boolean>;
  cancel: () => void;
}

function isEmptySelection(selection: SubjectSelection): boolean {
  return (
    selection.mode === 'SELECT_SUBJECTS' &&
    selection.positionIds.length === 0 &&
    selection.functionalActivityIds.length === 0
  );
}

function isNotOpenable(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 404 || status === 403;
}

export function useRecordConfig(matrixId: Ref<number>): UseRecordConfig {
  const router = useRouter();
  const { showErrorToast, showSuccessToast } = useCustomToast();

  const matrix = ref<RecordConfigMatrix | null>(null);
  const form = reactive<RecordConfigForm>(createEmptyForm());
  const isLoading = ref(false);
  const isSaving = ref(false);
  const loadFailed = ref(false);
  const loadedSnapshot = ref<string | null>(null);
  let isActive = true;

  const isGdOrigin = computed(() => matrix.value?.recordOrigin.name === GD_ORIGIN_NAME);
  const isErpOrigin = computed(() => matrix.value !== null && !isGdOrigin.value);
  const isIfApiType = computed(() => matrix.value?.recordType.name === EDITABLE_IF_API_TYPE_NAME);
  const showsIndexing = computed(() => isGdOrigin.value);

  const functionalInspectionTypeLabel = computed(() => {
    const type = matrix.value?.functionalInspectionType ?? null;
    return type === null ? null : FUNCTIONAL_INSPECTION_TYPE_LABELS[type];
  });

  const isDirty = computed(
    () => loadedSnapshot.value !== null && snapshotOf(form) !== loadedSnapshot.value
  );
  const hasMissingRequiredField = computed(
    () =>
      !hasRequiredRecordConfigFields(form, showsIndexing.value && !(matrix.value?.indexingLocked ?? false))
  );
  // The backend refuses an empty SELECT_SUBJECTS, and a reload would read it back as
  // "Não aplicável", so the save is held until the user picks someone or changes the mode.
  const hasEmptySubjectSelection = computed(
    () => isEmptySelection(form.archivingPermission) || isEmptySelection(form.notification)
  );
  // The backend answers 400 ARCHIVING_FIRST_ROUTE_REQUIRED for a model without a date route.
  const hasModelWithoutDateRoute = computed(
    () =>
      form.electronicArchivingModel !== null &&
      !buildPayload(form).archivingRoutes.some((route) => route.position === 1)
  );
  const saveBlockedReason = computed(() => {
    if (hasMissingRequiredField.value) return MISSING_REQUIRED_FIELDS_REASON;
    if (hasEmptySubjectSelection.value) return EMPTY_SUBJECT_SELECTION_REASON;
    if (hasModelWithoutDateRoute.value) return MISSING_DATE_ROUTE_REASON;
    return null;
  });
  const canSave = computed(
    () => !isLoading.value && !isSaving.value && isDirty.value && saveBlockedReason.value === null
  );

  const showsElectronicBlock = computed(() => hasElectronicFormat(form.format));
  const showsPhysicalBlock = computed(() => hasPhysicalFormat(form.format));
  const showsArchivingPermission = computed(() => hasArchivingPermission(form.format));

  function applyFixedArchivingPermission(): void {
    if (!hasArchivingPermission(form.format)) {
      form.archivingPermission = emptySubjectSelection();
    } else if (isErpOrigin.value) {
      form.archivingPermission = { ...AUTOMATIC_ARCHIVING };
    }
  }

  watch(
    () => form.hasStages,
    (hasStages) => {
      if (!hasStages) form.stageIds = [];
    }
  );

  watch(
    () => form.format,
    (format) => {
      if (!hasElectronicFormat(format)) {
        form.electronicArchivingModel = null;
        form.archivingRoutes = [];
      }
      if (!hasPhysicalFormat(format)) form.physicalLocationIds = [null];
      applyFixedArchivingPermission();
    }
  );

  watch(
    () => form.retention,
    (retention) => {
      if (retention !== 'DEFINED') {
        form.retentionMonths = null;
        form.retentionPeriods = [null];
      }
    }
  );

  function clearIndexingInputs(): void {
    form.indexingFieldNames = [];
    form.indexingSequenceStart = null;
    form.indexerFieldName = null;
  }

  // IF/API keeps the loaded indexer and titles, or every OS_OF config would open dirty. A locked
  // indexing stays as the records were made with, even empty, or the save would be refused as a change.
  function applyFixedIndexing(): void {
    if (matrix.value?.indexingLocked) return;
    if (isIfApiType.value && !isErpOrigin.value) {
      form.indexing = 'OS_OF';
      return;
    }
    if (isErpOrigin.value || form.indexing === 'OS_OF') {
      form.indexing = null;
      clearIndexingInputs();
    }
  }

  async function load(): Promise<void> {
    isLoading.value = true;
    try {
      const response = await getRecordConfig(matrixId.value);
      if (!isActive) return;
      matrix.value = response.matrix;
      Object.assign(form, formFromResponse(response));
      applyFixedArchivingPermission();
      applyFixedIndexing();
      await nextTick();
      loadedSnapshot.value = snapshotOf(form);
      setBreadcrumbLabel(`Configurar Registro ${response.matrix.code}`);
    } catch (error) {
      if (!isActive) return;
      loadFailed.value = true;
      showErrorToast({
        title: isNotOpenable(error) ? LOAD_FORBIDDEN_MESSAGE : LOAD_ERROR_MESSAGE,
        type: 'error',
        hideIcon: true,
      });
      router.replace(RECORD_LIST_ROUTE);
    } finally {
      isLoading.value = false;
    }
  }

  async function save(): Promise<boolean> {
    if (!canSave.value) return false;
    isSaving.value = true;
    try {
      await updateRecordConfig(matrixId.value, buildPayload(form));
      loadedSnapshot.value = null;
      showSuccessToast({ title: SAVE_SUCCESS_MESSAGE, type: 'success', hideIcon: true });
      await router.push(RECORD_LIST_ROUTE);
      return true;
    } catch (error) {
      showErrorToast({
        title: extractApiErrorMessage(error) ?? SAVE_ERROR_MESSAGE,
        type: 'error',
        hideIcon: true,
      });
      return false;
    } finally {
      isSaving.value = false;
    }
  }

  function cancel(): void {
    router.push(RECORD_LIST_ROUTE);
  }

  useUnsavedChangesGuard({
    isDirty,
    title: CONFIRM_CLOSE_TITLE,
    description: CONFIRM_CLOSE_DESCRIPTION,
    onDiscard: () => {
      loadedSnapshot.value = null;
    },
  });

  onUnmounted(() => {
    isActive = false;
    clearBreadcrumbLabel();
  });

  return {
    matrix,
    form,
    isLoading,
    isSaving,
    loadFailed,
    isGdOrigin,
    isErpOrigin,
    isIfApiType,
    showsIndexing,
    functionalInspectionTypeLabel,
    isDirty,
    canSave,
    saveBlockedReason,
    showsElectronicBlock,
    showsPhysicalBlock,
    showsArchivingPermission,
    load,
    save,
    cancel,
  };
}
